import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { isNothingVisible, calendarQueryIds, filterByVisible } from '../../lib/calendar-visibility';
import { Link } from 'react-router-dom';
import { Plus, Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, Filter, ChevronsLeft, ChevronsRight } from '../../icons';
import { layerConfirm, layerAlert, layerChoice } from '../../components/LayerPopup/LayerPopup';
import { cancelReservation } from '../../api/reservation';
import { roomsForDelete, roomDeleteQuestion, cancelRoomsAfterDelete } from './delete-rooms';
import {
  listEvents, deleteEvent, patchEvent, listCalendars, moveEvent,
  TYPE_COLOR_MAP, type CalendarEventDto, type CalendarEventType, type HolidayDto, type CalendarDto,
  type EventScope,
} from '../../api/calendar';
import { askRecurrenceScope, RecurrenceScopeHost } from './RecurrenceScopeDialog';
// ⭐ 2026-11-02 (지시 57) — 삭제하면 외부 참석자에게 취소 메일이 간다는 한 줄 · 보내는 중 알림.
import { externalCancelNotice, externalResultNotices } from './lib/externalAttendees';
import { toast } from '../../components/shared/Toast';
// ⭐ 2026-10-14 반복 규칙 1단계 — 수정 창 범위 잠금의 재료(이웃 회차).
import { occurrenceNeighborsIn } from './occurrence-neighbors';
import { describeCalendarError, friendlyErrorBody } from './calendarErrors';
import { TeamCalendarIdsProvider } from './EventCreatorAvatar';
import { EventTooltipProvider } from './eventTooltip';
import { useAuthStore } from '../../store/authStore';
import { useCalendarStore } from '../../store/calendarStore';
import { mergeTeamMemberEvents } from '../../lib/calendar-team-members';
import { MonthView, type MonthViewHandle } from './MonthView';
import { TimeGridStream, type TimeGridStreamHandle } from './TimeGridStream';
import { useCoalescedRange } from './useCoalescedRange';
import { unionIsoRange, type IsoRange } from './lib/rangeUnion';
import { DayAgendaView } from './DayAgendaView';
import { EventSearch } from './EventSearch';
import { HeaderDatePicker } from './HeaderDatePicker';
import { cachedHolidayYear, loadHolidayYear, yearsBetween } from './lib/holidayYearCache';
import { MonthYearPicker } from './MonthYearPicker';
import { CalendarSidebar } from './CalendarSidebar';
import { DayPopover } from './DayPopover';
import { TypeIcon } from './calendarTypeMeta';
import { dayTitleLabel, monthTitleLabel } from './lib/dateLabel';
import { stepMonth, stepDays, sameDayInMonth } from './lib/monthStep';
import { EventCreateModal } from './EventCreateModal';
import { EventDetailModal } from './EventDetailModal';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { tCalendar } from '../../lib/calendar-i18n';
import { useCalendarThemeStore, useCalendarThemeVars } from '../../lib/calendar-theme';
import { CAL_SHELL_ENABLED, calDateKey, eventsOnDay } from '../../lib/calendar-shell';
import { SHELL_RIGHTRAIL_GLOBAL } from '../../lib/layout-shell';
import { MEMO_PANEL_ENABLED } from '../../lib/memo-flag';
import { useMemoPanelStore } from '../../store/memoPanelStore';
import { RightRail } from '../../components/RightRail';
import { MyStatusAvatar } from '../../components/shared/Avatar';
import { CalendarMiniMonth } from './CalendarMiniMonth';
import { upcomingWindow, rangeCoversWindow, overlapsWindow } from './lib/upcomingWindow';
import { CalendarDayRail } from './CalendarDayRail';
import { CalendarResizer } from './CalendarResizer';
// ⭐ 2026-10-14 (반복 규칙 F) — 지난 회차의 「이후 일정」 이동 잠금.
import { isPastOccurrence } from './recurrence-past';
import {
  useCalendarShellStore,
  CAL_SIDE_MIN_WIDTH, CAL_SIDE_MAX_WIDTH, CAL_SIDE_DEFAULT_WIDTH,
  CAL_RAIL_MIN_WIDTH, CAL_RAIL_MAX_WIDTH, CAL_RAIL_DEFAULT_WIDTH,
} from '../../store/calendarShellStore';
import './CalendarPage.css';
import './CalendarShell.css';

/**
 * CalendarPage (P3-CAL-FE-2) — 시안 line 1309~ 매핑.
 *
 * 구조:
 *   - 좌측 사이드바 (240px): 새 일정 버튼 + 오늘 일정 리스트.
 *   - 우측 메인: 헤더 (월 네비 + Today + 뷰 토글) + 월간 그리드.
 *
 * 주간/일간 뷰는 placeholder — 후속 (P3-CAL-FE-2 follow-up).
 */
/** 팀 캘린더 id 집합 추출. teamId 는 falsy 검사(구버전 응답은 키 자체가 없다). */
function teamIdsOf(calendars: CalendarDto[]): ReadonlySet<string> {
  // org-team-calendar (migration 229 · 2026-10-13) — 조직도팀 캘린더도 «팀 캘린더»로 센다(아바타 매핑 등).
  return new Set(calendars.filter((c) => !!c.teamId || !!c.orgTeamId).map((c) => c.id));
}

export function CalendarPage() {
  const myUserId = useAuthStore((s) => s.user?.id ?? null);
  // ⑤ 우측 아이콘 레일의 프로필 슬롯용.
  const myName = useAuthStore((s) => s.user?.name ?? '');
  const myAvatarUrl = useAuthStore((s) => s.user?.avatarUrl ?? null);
  const activeView = useCalendarStore((s) => s.activeView);
  const setView = useCalendarStore((s) => s.setView);
  const visibleCalendarIds = useCalendarStore((s) => s.visibleCalendarIds);
  // 「팀원 일정도 보기」(2026-10-13) — 필터 팝오버의 스위치. 기본 꺼짐(store persist).
  const showTeamMembers = useCalendarStore((s) => s.showTeamMembers);
  const toggleShowTeamMembers = useCalendarStore((s) => s.toggleShowTeamMembers);
  const [holidays, setHolidays] = useState<HolidayDto[]>([]);
  // 작성자 아바타는 팀 캘린더 일정에만 붙는다 → 팀 캘린더 id 집합이 필요하다.
  // 사이드바는 필터 팝오버가 열릴 때만 렌더되므로 그 콜백에 의존할 수 없어 여기서 직접 조회한다.
  const [teamCalendarIds, setTeamCalendarIds] = useState<ReadonlySet<string>>(() => new Set());
  // cursor 의미: month 뷰=월초, week/day 뷰=기준일. 뷰별 range 계산이 분기.
  const [cursor, setCursor] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [events, setEvents] = useState<CalendarEventDto[]>([]);
  /**
   * 주간 «가로 연속 스크롤»이 요구한 조회 구간 (C-2b · 2026-09-29).
   *
   * ## ★ 누적 로더를 «새로 만들지 않았다» — 대신 «범위만 넓힌다»
   *
   * 정본은 `loaded` 범위를 들고 «델타만» 가져와 기존 목록에 합쳤다. 그 길은 **합치기에서 중복이
   * 생긴다** — 같은 일정이 두 구간에 걸치면 두 벌이 되고, 그것을 `occurrenceKey` 로 또 걸러야 한다.
   *
   * 여기서는 **구간을 union 해 `range` 로 넘기고, 기존 `refresh()` 가 그 한 구간을 통째로
   * 가져오게** 한다. 한 번에 한 요청이므로 **합칠 것이 없고 중복도 원리상 안 생긴다.**
   *   · 요청 «수»는 같다 — `useCoalescedRange` 가 스크롤이 멈출 때 한 번만 보낸다
   *   · 대가는 «바이트» — 델타(6주)가 아니라 union(최대 210일)을 다시 받는다
   *   · 그 210일은 `useCoalescedRange` 의 `MAX_SPAN_DAYS` 가 못 박고, 서버 상한 365일 아래다
   *
   * ⛔ `null` 이면 «스크롤이 아직 아무것도 요구하지 않았다»는 뜻이고, 그때는 종전 7일 구간을 쓴다.
   */
  const [streamRange, setStreamRange] = useState<IsoRange | null>(null);
  /** 스크롤로 화면 왼쪽에 걸린 날짜 — 주간 헤더 제목이 이것을 따라간다(a4-a). */
  const [visibleDay, setVisibleDay] = useState<Date | null>(null);
  const streamRef = useRef<TimeGridStreamHandle>(null);
  /**
   * ⭐ **M-2 — 스크롤로 화면 «위»에 걸린 달.** 월간 헤더 제목이 이것을 따라간다.
   * ⚠ 바로 위 `visibleDay`(주간)와 «같은 자리·같은 까닭»이다 — 스크롤로 다른 달이 보이는데
   *   제목이 `cursor` 의 달에 머물면 **어긋나 보인다.**
   * `null` = 아직 아무것도 안 알렸다 ⇒ 종전대로 `cursor` 를 쓴다.
   */
  const [visibleMonth, setVisibleMonth] = useState<{ y: number; m: number } | null>(null);
  const monthRef = useRef<MonthViewHandle>(null);
  /**
   * 빈 칸을 «끌어» 새 일정 만들기 (2026-10-01 · 정본 배선 그대로).
   *
   * ⭐ 끌린 시각을 그대로 창에 넘긴다 — 그러지 않으면 모달의 기본값이 이겨서 15:00 에서 끌어도
   *   엉뚱한 시각으로 열린다(그 기본값은 `CalendarPage/default-event-range.ts` 가 정한다).
   * ⚠ 이 길로 열 때만 시각을 채운다. 툴바·날짜클릭으로 여는 길은 한 글자도 안 바뀐다.
   */
  const openCreateOnSlot = useCallback((start: Date, end: Date) => {
    setCreateType(undefined);
    setCreateDate(start);
    setCreateStart(start);
    setCreateEnd(end);
    setCreating(true);
  }, []);
  /**
   * 뷰를 갈아타면 스크롤이 요구한 구간을 «버린다» — ⭐ 렌더 «중»에 버린다(effect 가 아니다).
   *
   * ⛔ 처음에는 `useEffect(…, [activeView])` 로 버렸고 **그것이 결함이었다**(시험으로 확인):
   *   뷰가 주간으로 바뀐 그 커밋에서 «껍데기의 마운트 레이아웃 effect»가 «먼저» 돌아 자기 창
   *   (91일)을 알리고, **그 뒤에** 이 passive effect 가 돌아 그 값을 «지워» 버린다
   *   ⇒ 화면은 91일을 그리는데 조회는 7일만 해서 **가장자리 일정이 안 보인다.**
   * ⇒ React 가 권하는 「렌더 중 상태 조정」으로 바꿨다 — 갈아치우기가 커밋 «전»에 끝나므로
   *   껍데기의 알림을 덮지 않고, 낡은 구간이 조회 effect 에 닿지도 않는다.
   */
  const [rangeOwnerView, setRangeOwnerView] = useState(activeView);
  if (rangeOwnerView !== activeView) {
    setRangeOwnerView(activeView);
    setStreamRange(null);
    setVisibleDay(null);
  }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventDto | null>(null);
  const [creating, setCreating] = useState(false);
  // 생성 시 클릭한 날짜(날짜 셀 클릭 생성). null 이면 현재 cursor 기준.
  const [createDate, setCreateDate] = useState<Date | null>(null);
  /**
   * 빈 칸을 «끌어» 만들 때의 시작·종료 (2026-10-01). 툴바·날짜클릭으로 열 때는 null 이라
   * 모달이 종전 기본값(09:00~10:00)을 쓴다 — 그 길은 한 글자도 안 바뀐다.
   */
  const [createStart, setCreateStart] = useState<Date | null>(null);
  const [createEnd, setCreateEnd] = useState<Date | null>(null);
  // T-cal27 — 수정 대상 이벤트(null=수정 모달 닫힘).
  const [editingEvent, setEditingEvent] = useState<CalendarEventDto | null>(null);
  // 캘린더 상세화(2026-06-07) — 생성 시 type 선지정(팝업 메뉴 → 일정/할일/기념일).
  const [createType, setCreateType] = useState<CalendarEventType | undefined>(undefined);
  // 날짜클릭 레이어 팝업(월뷰): 선택 날짜 + 앵커 위치. null=닫힘.
  const [dayPopover, setDayPopover] = useState<{ date: Date; anchor: DOMRect } | null>(null);
  // 헤더 캘린더·필터 드롭다운.
  const [filterOpen, setFilterOpen] = useState(false);
  // 헤더 제목 클릭 → 날짜 고르개(2026-09-14 사용자 요청).
  const [titlePickerOpen, setTitlePickerOpen] = useState(false);
  // ── 캘린더 3열 셸 (CAL_SHELL_ENABLED 일 때만 의미가 있다. OFF 면 읽는 곳이 없어 무해) ──
  // 선택일 — 우측 레일이 보여주는 날짜. 기본 오늘.
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  // 사이드바 열림. 좁은 화면(<1280px)에서는 아래 effect 가 접는다.
  const [sideOpen, setSideOpen] = useState(true);
  const narrowViewport = useMediaQuery('(max-width: 1279px)');
  // ⑤ 우측 아이콘 레일 열림(»/« 로 토글).
  const [railOpen, setRailOpen] = useState(true);
  // calendar-shell-resize — 사이드바/선택일 레일 폭(드래그 조절 + localStorage 영속).
  const sideWidth = useCalendarShellStore((s) => s.sideWidth);
  const railWidth = useCalendarShellStore((s) => s.railWidth);
  const setSideWidth = useCalendarShellStore((s) => s.setSideWidth);
  const setRailWidth = useCalendarShellStore((s) => s.setRailWidth);
  // ⑤ 레일 메모 버튼 → 전역 메모 패널 토글(design-20260724 §4.3).
  const openMemoPanel = useMemoPanelStore((s) => s.togglePanel);
  // 사이드바 '다가올 일정' 그룹 헤더용 캘린더 목록(이름·색).
  const [calendars, setCalendars] = useState<CalendarDto[]>([]);
  // 사이드바 「다가올 일정」 — 화면 조회가 받은 구간 · 그 구간이 못 덮을 때 따로 받은 일정(null=화면 것을 쓴다).
  const [loadedRange, setLoadedRange] = useState<{ from: string; to: string } | null>(null);
  const [upcomingExtra, setUpcomingExtra] = useState<CalendarEventDto[] | null>(null);
  // 캘린더 테마 — 활성 테마의 --cal-* 변수(루트 적용) + 최초 카탈로그/선택 로드.
  const calThemeVars = useCalendarThemeVars();
  const ensureCalTheme = useCalendarThemeStore((s) => s.ensureLoaded);
  useEffect(() => { void ensureCalTheme(); }, [ensureCalTheme]);

  const {
    shouldRender: shouldRenderCreate,
    isLeaving: isCreateLeaving,
  } = useModalLeave(creating, 200);
  const {
    shouldRender: shouldRenderDetail,
    isLeaving: isDetailLeaving,
    latched: detailSnap,
  } = useModalLeave(selectedEvent, 200);
  const {
    shouldRender: shouldRenderEdit,
    isLeaving: isEditLeaving,
    latched: editSnap,
  } = useModalLeave(editingEvent, 200);
  useBodyScrollLock(shouldRenderCreate || shouldRenderDetail || shouldRenderEdit);

  // 날짜 셀 클릭 → 그 날짜로 생성 모달(주/일 뷰의 빈 슬롯 등 직접 생성 경로).
  function openCreateOnDate(date: Date) {
    setCreateType(undefined);
    setCreateDate(date);
    setCreating(true);
  }

  // 월뷰 날짜 클릭 → 레이어 팝업(그날 항목 + 기능메뉴).
  function openDayPopover(date: Date, anchor?: DOMRect) {
    // 3열 셸 — 팝업을 띄우지 않는다. 우측 레일이 같은 정보를 상시 보여주므로 팝업은 화면만 가린다.
    // 유형별 생성(일정/할일/기념일/휴가)은 생성 모달 안의 유형 선택으로 대체된다.
    if (CAL_SHELL_ENABLED) { selectDay(date); return; }
    if (!anchor) { openCreateOnDate(date); return; }
    setDayPopover({ date, anchor });
  }

  // 팝업 기능메뉴 → 해당 type 으로 생성 모달.
  function createFromPopover(type: CalendarEventType) {
    const d = dayPopover ? new Date(dayPopover.date) : new Date();
    d.setHours(9, 0, 0, 0);
    setDayPopover(null);
    setCreateType(type);
    setCreateDate(d);
    setCreating(true);
  }

  /*
   * 할일 완료 토글(팝업 인라인) — optimistic + PATCH.
   *
   * ⚠ **회차를 안 가린다** (2026-09-30 실측). 낙관적 갱신도 `ev.id === e.id` 뿐이고,
   *   `patchEvent` 도 scope 를 «안 넘긴다» ⇒ 서버가 `all` 로 읽어 **master 를 고친다.**
   *   같은 결함의 갈래다 — 반복 일정의 회차를 눌러도 master 가 바뀐다.
   *
   * ⇒ **그런데 지금은 닿기 어렵다.** 서버 `serializeOccurrence` 가 회차의 `done` 을
   *   **`false` 로 박아** 둔다(「occurrence 에 done 개념 없음」 · `calendar-helpers.ts`).
   *   ⇒ 반복 일정은 할일(todo)이 아니어서 이 자리에 회차가 오지 않는다.
   *
   * ⭐ **만료 조건 — 반복 일정을 할일로 쓸 수 있게 되면 이 자리가 «열린다».**
   *   그때는 `EventCreateModal` 이 한 것처럼 회차를 가리거나 scope 를 넘겨야 한다.
   */
  async function toggleDone(e: CalendarEventDto) {
    const next = !e.done;
    setEvents((prev) => prev.map((ev) => (ev.id === e.id ? { ...ev, done: next } : ev)));
    try {
      await patchEvent(e.id, { done: next });
    } catch {
      await refresh();
    }
  }

  // 범위 계산 — 뷰별 분기(month=7x6 그리드, week=일~토 7일, day=하루).
  const range = useMemo(() => {
    const base = new Date(cursor);
    base.setHours(0, 0, 0, 0);
    if (activeView === 'day') {
      const end = new Date(base); end.setDate(base.getDate() + 1);
      return { from: base.toISOString(), to: end.toISOString() };
    }
    if (activeView === 'week') {
      // ★ 연속 스크롤이 요구한 구간이 있으면 «그것»이다(C-2b) — 없으면 종전 7일 그대로.
      //   `refresh()` 는 한 글자도 안 고쳤다. 범위만 넓혀 주면 그것이 그대로 가져온다.
      if (streamRange) return streamRange;
      const start = new Date(base); start.setDate(base.getDate() - base.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 7);
      return { from: start.toISOString(), to: end.toISOString() };
    }
    /*
     * ⭐ **M-1 — 월간도 «연속 스크롤»이 요구한 구간을 쓴다** (2026-10-02).
     *   주간과 «같은 자리·같은 꼴»이다(바로 위). `refresh()` 는 한 글자도 안 고친다.
     * ⛔ 없으면(아직 안 알렸으면) 종전 7×6 구간 그대로 — 아래가 그것이다.
     */
    if (activeView === 'month' && streamRange) return streamRange;
    // month — 7x6 그리드의 첫 일요일 (이전 달 일부 포함).
    const from = new Date(base);
    from.setDate(1);
    const firstDow = from.getDay();
    const gridStart = new Date(from);
    gridStart.setDate(from.getDate() - firstDow);
    const gridEnd = new Date(gridStart);
    gridEnd.setDate(gridStart.getDate() + 42);
    return { from: gridStart.toISOString(), to: gridEnd.toISOString() };
    // ⚠ streamRange 를 «의존성에 넣는 것»을 빠뜨려 실제로 겪었다 — 넓힌 구간이 상태에는 들어가고
    //   이 memo 는 옛 값을 돌려줘, 화면은 91일을 그리면서 조회는 7일만 했다(시험 ① 이 잡았다).
  }, [cursor, activeView, streamRange]);

  /**
   * ⭐ 「주간인데 껍데기가 아직 창을 안 알렸다」 — 그 한 렌더 동안은 «아무것도 안 받는다» (C-2b).
   *
   * 주간으로 갈아탄 커밋에서는 `streamRange` 가 아직 null 이라 위 memo 가 종전 7일 구간을
   * 돌려준다. 그 값으로 조회가 나가면 곧 91일 구간이 알려져 «같은 화면에 두 번» 받는다
   * (실측: 주간 전환 때 listEvents 2건 → 3건. `CalendarPage.test.tsx:272` 가 그 수를 잠그고 있다).
   * ⇒ 그 한 렌더만 기다린다. 껍데기의 마운트 레이아웃 effect 가 «같은 커밋 안에서» 알려 주므로
   *   기다림은 눈에 보이지 않는다(화면이 늦게 뜨지 않는다).
   */
  /*
   * ⭐ M-1 — **월간도 같은 기다림이 필요하다.** 월간이 연속 창이 된 뒤로는, 갈아탄 첫 렌더에
   *   `streamRange` 가 아직 null 이라 위 memo 가 «종전 7×6 구간»을 돌려준다. 그 값으로 조회가
   *   나가면 곧 넓은 구간이 알려져 **같은 화면에 두 번** 받는다(주간이 겪은 그것과 같은 꼴).
   * ⇒ ★ 그 한 렌더만 기다린다 — 껍데기의 마운트 레이아웃 effect 가 같은 커밋 안에서 알려 준다.
   */
  const streamRangePending = (activeView === 'week' || activeView === 'month') && streamRange === null;

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      /*
       * ⭐ 2026-09-30 — 판정을 `lib/calendar-visibility` 한 곳에 두었다.
       *   ⓑ 빈 고름([])은 «빈 결과»다 — 종전에는 undefined 로 떨어져 «전부»가 왔다(정반대).
       *   ⓐ 「없음」 통이 보이면 서버에 `calendarIds` 를 «안 보낸다» — 서버의 IN 이
       *     NULL 을 빼서 그 통이 통째로 사라지기 때문이다. 그 대신 화면이 가른다.
       */
      if (isNothingVisible(visibleCalendarIds)) { setEvents([]); return; }
      const calIds = calendarQueryIds(visibleCalendarIds);
      // ⭐ 「팀원 일정도 보기」(2026-10-13) — 켜졌을 때만 teamMembers=1 이 붙는다(꺼짐은 종전 요청 그대로).
      const res = await listEvents(range.from, range.to, calIds, undefined, { teamMembers: showTeamMembers });
      const got = Array.isArray(res.events) ? res.events : [];
      // ⚠ 팀원 일정은 팀원의 캘린더에 있어 내 체크박스에 없다 ⇒ 거르기 뒤에 살린다(lib/calendar-team-members).
      setEvents(mergeTeamMemberEvents(got, filterByVisible(got, visibleCalendarIds), {
        enabled: showTeamMembers,
        myUserId: myUserId !== null ? String(myUserId) : null,
        knownCalendarIds: calendars.map((c) => c.id),
      }));
      // ⭐ 다가올 일정 — 이 구간이 그 구간을 덮는지 가르려고 «받은 구간»을 적어 둔다(아래 effect).
      setLoadedRange({ from: range.from, to: range.to });
    } catch (err) {
      setError((err as Error)?.message ?? tCalendar('calendar.err.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (streamRangePending) return; // ★ 위 주석 — 껍데기의 창을 기다린다(요청 한 번을 아낀다)
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, visibleCalendarIds, streamRangePending, showTeamMembers]);

  /*
   * ⭐ 사이드바 「다가올 일정」 — 보고 있는 화면과 «무관하게» 오늘 00:00 ~ 다음 주 끝 (2026-10-13).
   *   ⛔ 종전에는 화면이 받은 일정을 그대로 썼다 ⇒ 다른 달을 보면 비거나 달 끝에서 잘렸다.
   *   ⭐ 다만 «화면이 이미 그 구간을 통째로 받았으면» 다시 받지 않는다 — 월간·주간 연속 창(±6주 / 91일)이
   *     오늘을 보고 있을 때가 그렇다. 덮지 못할 때만 `listEvents(오늘, 다음 주 끝)` 를 한 번 더 부른다.
   *   ⚠ 화면 쪽 조회가 다시 나갈 때(`events` 가 바뀔 때)마다 따라 다시 받는다 — 만들기·지우기 뒤 낡지 않게.
   */
  const upcomingTodayKey = calDateKey(new Date());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const upcomingWin = useMemo(() => upcomingWindow(new Date()), [upcomingTodayKey]);
  useEffect(() => {
    if (!CAL_SHELL_ENABLED) return;
    if (isNothingVisible(visibleCalendarIds)) { setUpcomingExtra(null); return; }
    if (!loadedRange) return;
    if (rangeCoversWindow(loadedRange, upcomingWin)) { setUpcomingExtra(null); return; }
    let cancelled = false;
    listEvents(upcomingWin.from.toISOString(), upcomingWin.to.toISOString(), calendarQueryIds(visibleCalendarIds))
      .then((res) => {
        if (cancelled) return;
        const got = Array.isArray(res?.events) ? res.events : [];
        setUpcomingExtra(filterByVisible(got, visibleCalendarIds));
      })
      .catch(() => { if (!cancelled) setUpcomingExtra([]); });
    return () => { cancelled = true; };
  }, [loadedRange, events, visibleCalendarIds, upcomingWin]);

  // 팀 캘린더 id 집합 — 최초 1회. 실패는 무시(아바타만 안 붙고 캘린더는 정상 렌더).
  // ⚠ teamId 는 `=== null` 이 아니라 falsy 검사 — 구버전 응답/mock 은 키를 아예 안 보낸다.
  useEffect(() => {
    let cancelled = false;
    listCalendars()
      .then((cals) => { if (!cancelled) setTeamCalendarIds(teamIdsOf(cals)); if (!cancelled) setCalendars(cals); })
      .catch(() => { /* 무시 */ });
    return () => { cancelled = true; };
  }, []);

  // 3열 셸 — 좁은 화면에서 사이드바 자동 접힘/복원(<1280px).
  // 임계폭을 넘나들 때만 강제하므로, 그 사이의 수동 토글(«/») 은 그대로 유지된다.
  useEffect(() => {
    if (!CAL_SHELL_ENABLED) return;
    setSideOpen(!narrowViewport);
  }, [narrowViewport]);

  // 공휴일 — 조회 범위에 걸친 연도들 fetch (KR). 실패는 무시(표시 레이어, 비치명적).
  // ⭐ 2026-10-30 — «연도마다 한 번만» 받는다(`lib/holidayYearCache`). 종전에는 `range.from · range.to`
  //   를 들어 연속 스크롤이 구간을 넓힐 때마다 같은 연도를 다시 받아 6101 에서 429 가 줄줄이 났다.
  //   ⇒ effect 는 «연도 집합»이 바뀔 때만 돌고, 화면의 `holidays` 는 캐시에서 합쳐 만든다.
  const holidayYearsKey = yearsBetween(range.from, range.to).join(',');
  useEffect(() => {
    if (streamRangePending) return; // ★ 같은 까닭 — 7일 구간으로 공휴일을 먼저 받지 않는다
    const years = holidayYearsKey ? holidayYearsKey.split(',').map(Number) : [];
    const merged = () => years.flatMap((y) => cachedHolidayYear(y, 'KR') ?? []);
    setHolidays(merged());
    const missing = years.filter((y) => cachedHolidayYear(y, 'KR') === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    void Promise.all(missing.map((y) => loadHolidayYear(y, 'KR')))
      .then(() => { if (!cancelled) setHolidays(merged()); });
    return () => { cancelled = true; };
  }, [holidayYearsKey, streamRangePending]);

  /**
   * 뷰별 이동 — month=±1월, week=±7일, day=±1일.
   *
   * ⛔ **달을 건너뛰던 것**(2026-09-28 운영에서 잡힘) — `cursor.setMonth(+1)` 은 cursor 의
   *   «일»을 그대로 두고 달만 바꾼다. cursor 가 10/31 이면 「11월 31일」이 되고 JS 가 그것을
   *   **12월 1일**로 넘겨, 「다음 달」 한 번에 **11월을 통째로 건너뛰었다**.
   *   ⇒ `stepMonth` 가 일을 1일로 두고 옮긴다(월간 cursor 의 뜻이 「월초」라 그 뜻과도 맞는다).
   *
   * ⭐ **레일도 함께 옮긴다** — 종전에는 `cursor` 만 움직여서, 10월을 보는데 레일은 계속
   *   「9월 15일」을 가리켰다. 「한 화면이 서로 다른 말을 한다」는 같은 병이다(2026-09-28 전환
   *   쪽을 먼저 고쳤고, 이쪽이 그 나머지다).
   *   · 달 이동 — 「새 달의 «같은 날짜»」. 없는 날이면 그 달 마지막 날(사용자 결정).
   *   · 주 이동 — cursor 와 «같은 만큼»(±7일) 옮기면 요일과 그 주 안 위치가 함께 유지된다.
   *   · 일 이동 — 레일이 **없다**(c9: 일간에서는 안 그린다) ⇒ 건드리지 않는다.
   */
  function navigate(delta: number) {
    if (activeView === 'month') {
      const next = stepMonth(cursor, delta);
      if (CAL_SHELL_ENABLED) setSelectedDate(sameDayInMonth(selectedDate, next));
      setCursor(next);
      return;
    }
    if (activeView === 'week') {
      setCursor(stepDays(cursor, delta * 7));
      if (CAL_SHELL_ENABLED) setSelectedDate(stepDays(selectedDate, delta * 7));
      return;
    }
    setCursor(stepDays(cursor, delta));
  }

  function goToToday() {
    const d = new Date();
    // month 뷰는 월초로 정렬, week/day 는 오늘 그대로.
    if (activeView === 'month') d.setDate(1);
    d.setHours(0, 0, 0, 0);
    setCursor(d);
    // 3열 셸 — 우측 레일도 오늘로. (OFF 면 읽는 곳이 없어 무해)
    const today = new Date(); today.setHours(0, 0, 0, 0);
    setSelectedDate(today);
  }

  /**
   * 3열 셸 — 선택일 변경(미니 캘린더 / 다가올 일정 / 월 그리드 공통).
   * 다른 달의 날짜를 고르면 본체 그리드도 그 달로 따라간다.
   */
  function selectDay(date: Date) {
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    setSelectedDate(d);
    if (activeView === 'month' && (d.getMonth() !== cursor.getMonth() || d.getFullYear() !== cursor.getFullYear())) {
      const m = new Date(d); m.setDate(1); m.setHours(0, 0, 0, 0);
      setCursor(m);
    }
  }

  // 3열 셸 — 미니 캘린더 점 표시용(일정이 하루라도 걸친 날짜 키 집합).
  /*
    필터(2026-09-09 사용자 결정 · 2026-09-28 반영) — 유형 끄기 + 「내가 포함된 일정만」.

    ⚠ **판정은 정본 그대로다.** 「내가 포함된」은 작성자거나 참석자이거나 팀원인 것이다
      — 그 셋을 한 곳(`eventAssignment.isEventAssignedToMe`)에서 본다.
      ⛔ 다만 그 함수는 `myUserId == null` 이면 «참»을 준다(렌더 쪽 「채움이 기본」 관례).
        여기 필터는 반대여야 한다 — 판정 불가면 «걸러내는» 쪽이 안전하다(정본 :139 도 그렇다).
        목적이 다른 두 판정을 합치지 않는다.

    ⚠ `visibleEvents` 를 새로 만들고 «넘기는 곳»만 바꾼다 — `events`(서버에서 받은 것)는
      그대로 둔다. 필터는 화면에만 걸고 조회는 안 바꾼다(회귀 안전).
  */
  const onlyMine = useCalendarStore((s) => s.onlyMine);
  const toggleOnlyMine = useCalendarStore((s) => s.toggleOnlyMine);
  const toggleType = useCalendarStore((s) => s.toggleType);
  const hiddenTypeList = useCalendarStore((s) => s.hiddenTypes);
  const hiddenTypes = useMemo(() => new Set(hiddenTypeList), [hiddenTypeList]);
  const myUserIdStr = myUserId !== null ? String(myUserId) : null;
  const isIncludedForMe = useMemo(() => (e: CalendarEventDto) => (
    myUserIdStr !== null
    && (
      e.creatorId === myUserIdStr
      || e.attendees.some((a) => a.userId === myUserIdStr)
      || (e.teamMembers?.some((m) => m.userId === myUserIdStr) ?? false)
    )
  ), [myUserIdStr]);
  const visibleEvents = useMemo(() => {
    let list = hiddenTypes.size === 0 ? events : events.filter((e) => !hiddenTypes.has(e.type));
    if (onlyMine) list = list.filter(isIncludedForMe);
    return list;
  }, [events, hiddenTypes, onlyMine, isIncludedForMe]);
  /** 검색이 «현재 필터를 존중»하게 — 그리드와 «같은 술어»를 EventSearch 에 넘긴다(2026-09-09). */
  const searchFilter = useMemo(() => (e: CalendarEventDto) => (
    !hiddenTypes.has(e.type) && (!onlyMine || isIncludedForMe(e))
  ), [hiddenTypes, onlyMine, isIncludedForMe]);
  /** 사이드바 「다가올 일정」 — 그리드와 «같은 필터»(유형·내 일정만)를 걸고 그 구간에 걸친 것만. */
  const upcomingEvents = useMemo(() => (upcomingExtra ?? events).filter((e) => (
    searchFilter(e) && overlapsWindow(e.startAt, e.endAt, upcomingWin)
  )), [upcomingExtra, events, searchFilter, upcomingWin]);

  /**
   * 날짜 고르개에서 «주 한 줄» 또는 «하루»를 골랐을 때 (주간·일간).
   * 주간이면 그 줄의 일요일이, 일간이면 그 날짜가 그대로 넘어온다(HeaderDatePicker).
   * ⭐ 2026-10-30 — 주간도 «누른 그 날»이 넘어온다. 주간 range·스크롤은 그 날이 든 주로 간다.
   *
   * ⭐ cursor 와 선택일을 «함께» 옮긴다 — 이 레포가 2026-09-28 세 판에 걸쳐 세운 불변식이다
   *   (뷰를 바꿔도 · 이전/다음으로 옮겨도 「레일이 보고 있는 것과 어긋나지 않는다」).
   *   하나만 옮기면 「한 화면이 서로 다른 말을 한다」로 되돌아간다.
   * ⚠ 정본은 여기서 연속 스크롤(`streamViewRef.scrollToDate`)도 부르는데 main 에 그 뷰가 없다(Ⓑ⑤).
   */
  function jumpToDate(date: Date) {
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    setCursor(d);
    if (CAL_SHELL_ENABLED) setSelectedDate(d);
    setTitlePickerOpen(false);
  }

  /**
   * 날짜 고르개에서 «연/월»을 골랐을 때 (월간).
   * ⚠ 그 달 1일로 간다 — 고른 것이 「달」이라 「그 달의 어떤 날」을 지어내지 않는다.
   *   ⇒ 그래서 레일도 그 달 1일을 가리킨다(위 불변식 — 둘이 같은 달을 말해야 한다).
   */
  function jumpToMonth(year: number, month: number) {
    const d = new Date(year, month, 1); d.setHours(0, 0, 0, 0);
    setCursor(d);
    if (CAL_SHELL_ENABLED) setSelectedDate(d);
    setTitlePickerOpen(false);
  }

  /**
   * 검색 결과를 골랐을 때 — 그 날짜로 옮기고 상세를 연다.
   * ⚠ 정본은 여기서 연속 스크롤(`streamViewRef.scrollToDate`)을 함께 부르는데 main 에 그 뷰가
   *   없다(Ⓑ⑤). 「그 날짜로 옮긴다」는 뜻만 같게 두고 스크롤 호출은 뺐다.
   */
  function handleSearchSelect(e: CalendarEventDto) {
    const d = new Date(e.startAt); d.setHours(0, 0, 0, 0);
    if (activeView === 'month') {
      selectDay(d);            // 다른 달이면 그리드도 그 달로 따라간다
    } else {
      setCursor(d);
      if (CAL_SHELL_ENABLED) setSelectedDate(d);
    }
    setSelectedEvent(e);
  }

  const eventDays = useMemo(() => {
    const s = new Set<string>();
    if (!CAL_SHELL_ENABLED) return s;
    for (const e of visibleEvents) {
      const start = new Date(e.startAt); start.setHours(0, 0, 0, 0);
      const end = new Date(e.endAt);
      // 멀티데이는 걸친 날 전부 표시. 방어적으로 상한(62일)을 둬 비정상 endAt 에도 루프가 끝난다.
      for (let i = 0, d = new Date(start); d <= end && i < 62; i++, d.setDate(d.getDate() + 1)) {
        s.add(calDateKey(d));
      }
    }
    return s;
  }, [events]);

  /**
   * 뷰 전환 — 「보던 날짜」를 그대로 들고 간다.
   *
   * ⛔ 종전에는 `setView(v)` 한 줄이었고, 위 주석만 「전환 시 cursor 를 적절히 보정」한다고
   *   «적고» 있었다. 실제로는 아무것도 보정하지 않아, 월간에서 주간/일간으로 넘어가면
   *   «늘 그 달 1일»이 열렸다 — cursor 의 뜻이 뷰마다 다르기 때문이다
   *   (월간=월초 · 주간/일간=기준일). 2026-09-28 운영에서 잡혔다.
   *
   * ⭐ 「월간이 보고 있는 날」이 무엇인지는 지어내지 않았다 — 선택일(`selectedDate`)이
   *   이미 그것이고, 우측 레일이 그 날짜를 «글자로» 띄우고 있다(월 그리드에서 날짜를
   *   누르면 바뀐다). 실측: 월간에서 10/15 를 누르면 레일은 「10월 15일 (목)」인데
   *   일간을 누르면 「10월 1일」이 열렸다 — 한 화면이 서로 다른 말을 하고 있었다.
   *
   * ⚠ 여섯 방향을 다 눌러 봤고 깨진 것은 월→주 · 월→일 둘뿐이다(주/일 → 월 은 이미 맞다).
   *   그래도 아래 `else` 로 «선택일을 심는다» — 이게 없으면 「일간 11/5 → 월간 → 일간」이
   *   선택일(여태 오늘에 멈춰 있다)로 «튄다». 한 방향만 고치면 새 결함이 생기는 자리다.
   */
  function switchView(v: 'month' | 'week' | 'day') {
    if (CAL_SHELL_ENABLED && activeView === 'month' && v !== 'month') {
      // 선택일이 «보고 있는 달 안»에 있을 때만 쓴다. 달만 옮기고 아무 날도 고르지 않은
      // 상태에서는 선택일이 다른 달에 있으므로 종전대로 1일이 열린다(회귀 없음).
      if (selectedDate.getFullYear() === cursor.getFullYear()
        && selectedDate.getMonth() === cursor.getMonth()) {
        const d = new Date(selectedDate); d.setHours(0, 0, 0, 0);
        setCursor(d);
      }
    } else if (CAL_SHELL_ENABLED && activeView !== 'month' && v === 'month') {
      const d = new Date(cursor); d.setHours(0, 0, 0, 0);
      setSelectedDate(d);
    }
    setView(v);
  }

  // 특정 날짜에 걸친 항목(팝업용) — 멀티데이 포함(start < dayEnd && end > dayStart).
  function eventsOnDate(date: Date): CalendarEventDto[] {
    const dayStart = new Date(date); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart); dayEnd.setDate(dayStart.getDate() + 1);
    return events.filter((e) => {
      const s = new Date(e.startAt); const en = new Date(e.endAt);
      return s < dayEnd && en > dayStart;
    });
  }

  // 헤더 라벨 — 뷰별.
  /**
   * ⭐ 2026-10-19 — 헤더에 걸린 주(일~토). 주간 화면의 하루종일 줄 높이가 «이 주에서 가장 많은 날»을 따른다
   *   (사용자 지시). 주간 제목도 이 주를 쓴다 — 화면 왼쪽에 걸린 날(visibleDay), 없으면 cursor.
   */
  const headerWeek = useMemo(() => {
    const anchor = visibleDay ?? cursor;
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - anchor.getDay());
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    return { start, end };
  }, [visibleDay, cursor]);
  const headerLabel = useMemo(() => {
    if (activeView === 'day') {
      // 2026-09-28 — 「년·월·일」이 글자로 박혀 있어 en·ja 에서도 한국어가 나왔다.
      //   ko 글자는 그대로이고(재서 확인) locale 만 따라간다 — `lib/dateLabel.ts` 참조.
      return dayTitleLabel(cursor);
    }
    if (activeView === 'week') {
      // ⭐ 2026-10-19 — 주 범위는 위 `headerWeek` 하나로 센다(하루종일 줄 높이도 그 주를 따른다).
      // ★ a4-a — 「가로 스크롤·이전/다음 뒤에 헤더가 왼쪽 날짜와 어긋난다」(사용자 리포트).
      //   스크롤로 다른 주가 보이는데 제목이 `cursor` 의 주에 머물면 그 증상이다.
      //   ⇒ 스크롤이 알려 준 «화면 왼쪽에 걸린 날짜»를 기준으로 삼는다. 없으면 종전대로 cursor.
      const { start, end } = headerWeek;
      // ⚠ 주간 제목은 «숫자 압축»(「9.27 ~ 10.3」)이라 한국어가 박혀 있지 않다 ⇒ 그대로 둔다.
      //   Intl 로 옮기면 ko 가 「9. 27. ~ 10. 3.」로 «바뀐다» — 그건 화면 모양 결정이다.
      return `${start.getMonth() + 1}.${start.getDate()} ~ ${end.getMonth() + 1}.${end.getDate()}`;
    }
    /*
     * ⭐ **M-2 — 월간 제목도 «화면에 걸린 달»을 따라간다** (주간의 바로 위 줄과 같은 까닭).
     *   연속 스크롤이라 ⛔ `cursor` 에 머물면 스크롤로 11월을 보는데 제목이 10월인 꼴이 된다.
     * ⚠ 아직 아무것도 안 알렸으면(`null`) 종전대로 `cursor` — 첫 렌더가 안 흔들린다.
     */
    const anchorMonth = visibleMonth ? new Date(visibleMonth.y, visibleMonth.m, 1) : cursor;
    return monthTitleLabel(anchorMonth);
  }, [cursor, activeView, headerWeek, visibleMonth]);

  /**
   * 연속 스크롤이 「이 구간이 필요하다」고 할 때 — 묶어서 «한 번만» 넓힌다 (C-2b).
   *
   * ⛔ `useCoalescedRange` 로 감싸지 않으면 가장자리를 붙잡고 있을 때 초당 십수 건이 나간다
   *   (정본 실측: 5초에 74건 · 사용자별 분당 한도를 캘린더 혼자 다 써 «다른 화면까지 429»).
   * ★ 넓힐 때 union 한다 — 과거로 갔다 미래로 가도 그 사이가 비지 않는다. 210일 상한은
   *   `useCoalescedRange` 가 못 박는다(서버 상한 365일 아래).
   */
  const applyStreamRange = useCallback(async (from: Date, to: Date) => {
    // ★ 넓히기는 `lib/rangeUnion` 한 곳에 둔다 — 「이미 덮었으면 같은 객체」가 요청 하나를 아끼고,
    //   그 규칙은 시험으로 잠가 둔다(`rangeUnion.test.ts`).
    setStreamRange((prev) => unionIsoRange(prev, from, to));
  }, []);
  const requestStreamRange = useCoalescedRange(applyStreamRange, 150);

  /**
   * 이전/다음·오늘·날짜 고르개로 `cursor` 가 옮겨지면 «그 날짜로 스크롤»한다.
   * ⛔ 껍데기를 다시 마운트하지 않는다 — `initialDate` 는 마운트 때만 쓰이므로 ref 로 뛴다
   *   (그러지 않으면 스크롤로 쌓아 둔 날짜 창이 매번 버려진다).
   */
  useEffect(() => {
    if (activeView !== 'week') return;
    streamRef.current?.scrollToDate(cursor);
  }, [cursor, activeView]);

  /**
   * ⭐ **M-2 — 월간도 «미끄러져 간다»** (2026-10-02 사용자 결정 ⓐ).
   *
   * 바로 위 주간과 **같은 꼴**이다. 「이전/다음·오늘·날짜 고르개」가 `cursor` 를 옮기면
   * 껍데기를 다시 마운트하지 않고 **그 날짜로 스크롤**한다.
   * ⚠ 멀리(창 밖 · 약 5~6개월) 뛰면 훅이 창을 새로 지어 **즉시 뛴 것처럼** 보인다 —
   *   `MonthViewHandle` 머리말에 그 경계를 적어 두었다. ⛔ 버그가 아니다.
   */
  useEffect(() => {
    if (activeView !== 'month') return;
    /*
     * ⛔⛔ **`cursor` 를 «그대로» 넘기면 안 된다 — 제목이 한 달 앞서 간다.** (재서 찾았다)
     *
     * 훅의 `scrollToDate` 는 점프 뒤 제목을 **「그다음 주(두 번째 줄)」**로 정한다. 그 어림은
     * 「그 **달**로 간다」를 가정한 것이다 — 달의 첫 주는 앞 달 날짜로 더 채워질 수 있어서
     * (월 1일이 토요일이면 그 주의 6일이 앞 달이다) 두 번째 줄을 보는 쪽이 안정적이기 때문이다.
     * ```
     *   그런데 일간(10/31)에서 월간으로 오면 cursor 는 ★ «10/31» 이다
     *   ⇒ 그 주(10/25~10/31)의 «그다음 주»는 11/1~11/7 ⇒ ⛔ 제목이 «11월»이 된다
     *   ⇒ ⇒ 사용자는 10월을 보고 있는데 머리글만 11월이다
     * ```
     * ⇒ ★ 월간의 이동 단위는 «달»이다 ⇒ ⭐ **그 달의 1일**로 뛴다. 그러면 그 어림이 제 뜻대로 선다.
     * ⚠ 보여 주는 날짜(선택일·레일)는 `cursor` 그대로다 — 여기서 바꾸는 것은 «어디로 스크롤하나»뿐이다.
     */
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    monthRef.current?.scrollToDate(monthStart);
  }, [cursor, activeView]);
  const isCreatorOfSelected = detailSnap && myUserId !== null && detailSnap.creatorId === String(myUserId);

  /**
   * calendar-shell-resize — 루트 인라인 스타일.
   * 기존 테마 변수(--cal-*)에 패널 폭 변수만 얹는다. 셸 OFF 면 읽는 CSS 규칙이 없으므로
   * 기존과 동일하게 테마 변수만 넘겨 인라인 스타일을 늘리지 않는다.
   */
  const shellStyle = useMemo<CSSProperties>(() => (
    CAL_SHELL_ENABLED
      ? { ...calThemeVars, '--cal-side-w': `${sideWidth}px`, '--cal-rail-w': `${railWidth}px` } as CSSProperties
      : calThemeVars
  ), [calThemeVars, sideWidth, railWidth]);

  /** ⭐ 2026-11-02 (지시 57) — 다음 범위 묻기에 한 번 덧붙일 줄. pickScope 가 읽고 비운다. */
  const scopeNotesRef = useRef<Partial<Record<EventScope, string>> | null>(null);
  /**
   * 반복 일정이면 적용 범위를 먼저 묻는다. 단일 일정이면 물을 게 없어 'all'.
   * 취소(ESC·배경·취소 버튼)면 null 을 돌려 호출부가 아무것도 하지 않게 한다.
   *
   * backend 는 예전부터 ?scope=this|future|all 을 받았지만 프론트가 안 보내 늘 'all' 이었다 —
   * 반복 중 하루만 지워도 시리즈 전체가 날아가던 경로가 여기서 끊긴다.
   */
  async function pickScope(
    e: CalendarEventDto,
    action: 'delete' | 'move',
    disabled?: Partial<Record<EventScope, string>>,
    hints?: Partial<Record<EventScope, string>>,
  ): Promise<EventScope | null> {
    // ⭐ 2026-11-02 (지시 57) — 이번 한 번 덧붙일 줄(삭제 → 외부 참석자 취소 메일). 읽으면 곧바로 비운다.
    const notes = scopeNotesRef.current;
    scopeNotesRef.current = null;
    if (!e.recurrenceRule) return 'all';
    return askRecurrenceScope({ action, title: e.title, disabled, ...(hints ? { hints } : {}), ...(notes ? { notes } : {}) });
  }

  /**
   * scope 가 가리키는 occurrence 들만 골라낸다 — 삭제(숨기기)·이동(옮기기) 양쪽이 같은 기준을 쓴다.
   *   this   → id + occurrenceAt 정확히 일치하는 한 줄
   *   future → id 일치 + occurrenceAt 이 이 회차 이상(문자열 비교 — ISO 8601 은 사전식=시간순)
   *   all    → id 만 일치(단일 이벤트든 반복 마스터의 모든 회차든 전부)
   */
  function matchesScope(ev: CalendarEventDto, e: CalendarEventDto, scope: EventScope): boolean {
    if (ev.id !== e.id) return false;
    if (scope === 'this') return ev.occurrenceAt === e.occurrenceAt;
    if (scope === 'future') return !!ev.occurrenceAt && !!e.occurrenceAt && ev.occurrenceAt >= e.occurrenceAt;
    return true;
  }

  async function onDelete(e: CalendarEventDto) {
    /*
     * ⭐⭐ 2026-10-06 — **낡은 막음을 «걷었다»**(사용자 결정, 관제 중계).
     *
     * ```
     * 2026-09-14  「뒤에 따로 고친 회차가 있으면 서버가 409」 ⇒ 눌러 보고 실패하게 두지 않고 미리 잠갔다
     * ⓧ #2204    서버가 ★ 막지 않고 «데려간다» — 삭제 쪽은 그 예외 «행을 지운다»
     *            ⇒ RECURRENCE_FUTURE_HAS_EXCEPTIONS 를 «내는 곳» 2 → ★ 0
     * ⭐ 2026-10-06  ⇒ 그래서 ★ 미리 막을 «까닭이 없어졌다» ⇒ 인자를 떼었다
     * ```
     * ⇒ ★ 함께 걷은 것 셋: `recurrenceGuards.hasLaterException`(파일째) ·
     *   `calendar.recurrence.future_has_exceptions_blocked` 글귀 · 그리고 이 인자.
     * ⚠ 되돌려야 할 날의 조건: 서버가 그 코드를 «다시 내기 시작하면».
     *   재는 법 — `git grep -c RECURRENCE_FUTURE_HAS_EXCEPTIONS -- apps/chat-server/src` 가 0 이 아니게 되면.
     */
    /*
     * ⭐ 2026-11-02 (지시 57) — 보낸 적 있는 외부 참석자가 있으면 확인 창에 「취소 메일이 발송됩니다」 한 줄.
     *   상세에서 이미 읽었으면 그 값을, 아니면 한 번 읽는다. 못 읽으면 null — 확인 창은 종전 그대로다.
     */
    const extNotice = await externalCancelNotice(e.id).catch(() => null);
    // 반복이면 범위 설명 «아래에 덧붙인다»(notes) — 기본 설명을 바꾸는 hints 는 쓰지 않는다. 이 한 번만 쓰고 비운다.
    scopeNotesRef.current = extNotice ? { this: extNotice, future: extNotice, all: extNotice } : null;
    const scope = await pickScope(e, 'delete', undefined);
    if (!scope) return;
    // 단일 일정은 종전대로 예/아니오 확인만. 반복은 범위 선택 자체가 확인 단계라 두 번 묻지 않는다.
    if (scope === 'all' && !e.recurrenceRule) {
      if (!(await layerConfirm(extNotice ? `${tCalendar('calendar.confirm.delete')}\n${extNotice}` : tCalendar('calendar.confirm.delete')))) return;
    }
    /*
     * ⭐ 2026-10-23 사용자 지시 — 잡힌 방이 있으면 «함께 취소할지» 묻는다(`delete-rooms.ts`).
     *   예 = 지운 뒤 예약도 취소 · 아니오 = 예약은 남긴다 · 닫기(×·ESC) = 일정 삭제도 그만.
     */
    const rooms = roomsForDelete(e, scope, events);
    let cancelRooms = false;
    if (rooms.length > 0) {
      const q = roomDeleteQuestion(rooms, scope, !!e.recurrenceRule);
      const ans = await layerChoice(q.message, { title: q.title, confirmText: q.yes, cancelText: q.no });
      if (ans === null) return;
      cancelRooms = ans === 'yes';
    }
    setSelectedEvent(null);
    // 낙관적으로 숨긴다 — 실패하면 아래 refresh 가 서버 상태로 되돌린다.
    //
    // ⚠ 정본은 여기에 `useDeferredCommit` 5초 유예(실행 취소 토스트)를 걸어 요청 자체를 미룬다.
    //   그 hook 도 `calendar.undo.*` 낱말도 main 에 «없어» 이 판에서는 빼고 곧바로 보낸다.
    //   ⇒ 되살릴 조건(재는 법): `git grep -c useDeferredCommit -- apps/frontend/src` 가 0 이 아니게 되면,
    //     즉 그 hook 이 들어오는 판에서 이 블록을 정본대로 deferCommit 으로 감싸고
    //     RecurrenceScope.test.tsx 뒤쪽 통(`CalendarPage — 반복 일정 삭제 범위`)을 이어 붙인다.
    /*
     * ⛔⛔ **방은 «안 놓인다»** — 지우기 «전»에 세어 둔다 (2026-10-01 운영 육안 확인).
     *
     * 사용자 원문: 「일정에서 회의실 잡아 놓은 일정을 삭제했는데 나의 예약에서 «승인 대기»로 나온다」
     * ```
     *   일정 삭제는 soft delete(deletedAt) ⇒ FK 의 ON DELETE SET NULL 이 «한 번도 안 터진다»
     *   ⇒ 예약은 calendar_event_id 를 들고 «계속 잡고 있다». 그것을 취소하는 코드가 아무 데도 없다
     * ```
     * ⇒ ★ 고치는 것은 «서버»다(그 쪽이 범위 this/future/all 를 안다). 여기서는 **말해 준다** —
     *   아무 말도 없으면 사용자는 「방도 놓였다」고 믿고 회의실이 계속 잡혀 있다.
     * ⚠ `refresh()` 뒤에는 그 일정이 목록에서 사라져 `e.reservations` 를 다시 볼 수 없다 ⇒ 먼저 센다.
     * ⚠ 서버가 주는 `reservations` 는 이미 「잡고 있는 것」만이다(status in pending·approved).
     */
    setEvents((prev) => prev.filter((ev) => !matchesScope(ev, e, scope)));
    try {
      if (extNotice) {
        // ⭐ 2026-11-02 (지시 57) — 취소 메일 «보내는 중»을 알린다(외부 참석자가 있을 때만 손잡이를 넘긴다).
        await deleteEvent(e.id, { scope, occurrenceAt: e.occurrenceAt }, (env) => {
          for (const n of externalResultNotices(env)) (n.variant === 'error' ? toast.error : toast.info)(n.text);
        });
      } else {
        await deleteEvent(e.id, { scope, occurrenceAt: e.occurrenceAt });
      }
      // ⭐ 2026-10-23 — 알릴 것은 «취소를 고르고도 못 한 것»뿐이다. 「유지」를 고른 방은 사용자가 고른 것이라 알리지 않는다.
      const left = cancelRooms ? rooms.length - (await cancelRoomsAfterDelete(rooms, cancelReservation)) : 0;
      await refresh();
      if (left > 0) {
        void layerAlert(tCalendar('calendar.delete.rooms_not_cancelled'), {
          title: tCalendar('calendar.delete.rooms_not_cancelled_title'),
        });
      }
    } catch (err) {
      await refresh();
      // 2026-09-15 — "삭제 실패: <서버 문구>" 대신 제목(무엇이) + 원인·해결 방법.
      const fe = describeCalendarError(err, 'delete');
      void layerAlert(friendlyErrorBody(fe), { title: fe.title });
    }
  }

  // 드래그 이동(T-cal21) — startAt/endAt 를 deltaMinutes 만큼 평행이동 후 PATCH.
  // 반복 occurrence 는 범위를 물어 scope=this/future/all 로 나간다(예전에는 조용히 무시했다).
  async function handleEventMove(e: CalendarEventDto, deltaMinutes: number) {
    const ms = deltaMinutes * 60_000;
    const newStart = new Date(new Date(e.startAt).getTime() + ms).toISOString();
    const newEnd = new Date(new Date(e.endAt).getTime() + ms).toISOString();

    /*
     * ⭐⭐ 2026-10-06 — **이 사유가 «깨어났고», 둘 중 «하나만» 깨웠다.**
     *
     * 사용자 결정으로 `scopesFor` 가 다시 셋을 돌려주므로 「이후 일정 모두」가 화면에 «뜬다»
     * ⇒ 이 미리막음이 ★ 다시 «보이는» 자리가 됐다. ⚠ 그런데 종전 두 사유의 **수명이 달랐다**:
     *
     * ```
     * ① 앞당기는 이동(deltaMinutes < 0)  ⇒ ★ ✅ «여전히 유효» — 잘라낸 앞 시리즈와 겹쳐 서버가 400
     *    ⇒ ⇒ 그래서 ⭐ «살려서» 깨웠다
     * ② 뒤에 따로 고친 회차가 있음        ⇒ ⛔ «낡았다» — ⓧ(#2204)로 서버가 막지 않고 데려간다
     *    ⇒ ⇒ 그래서 ⭐ 깨우지 «않고 걷었다»(hasLaterException 파일째 · 그 글귀 · 이 가지)
     * ```
     * ⇒ ★ 이것이 #2209 에서 내가 적어 둔 경고 그대로다 — 「한 덩이로 보고 둘 다 살리거나
     *   둘 다 지우지 말 것」. ⇒ ⇒ 그 경고가 ⭐ 이 판에서 값을 했다.
     *
     * ⚠ 되돌릴 조건: ② 를 되살려야 하는 날은 서버가 그 코드를 «다시 낼» 때다.
     *   재는 법 — `git grep -c RECURRENCE_FUTURE_HAS_EXCEPTIONS -- apps/chat-server/src` ≠ 0.
     */
    const futureBlocked = deltaMinutes < 0
      ? tCalendar('calendar.recurrence.future_backward_blocked')
      : null;
    /*
     * ⭐ 2026-10-14 (반복 규칙 F · 사용자 확정) — 끌어 옮기기는 늘 날짜·시간 변경이다:
     *   이후·전체 — 지난 회차(시작의 현지 날짜 < 오늘)를 끌었으면 지난 회차는 그대로 두고 오늘 이후 일정부터 옮겨진다
     *     (안내 hint_all_move · 2026-10-15 「이후」 잠금을 걷었다 — 아래)
     *   이 일정만 — 지난 회차라도 잠그지 않는다(순서 규칙만)
     *   (종전 1단계 B 의 「전체 일정 잠금」(all_time_change_blocked)은 이 규칙으로 걷었다.)
     */
    /*
     * ⭐ 2026-10-15 (사용자 결정) — 지난 회차를 끌어도 「이후 일정」을 잠그지 않는다. 「전체」처럼 지난 회차는 그대로 두고
     *   오늘 이후 일정부터 옮겨진다(서버가 같은 길로 처리) ⇒ 그 아래 안내를 그 말로 바꾸고, 앞당기기 잠금도 걸지 않는다.
     */
    const pastOpened = isPastOccurrence(e.occurrenceAt);
    const futureReason = pastOpened ? null : futureBlocked;
    // ⭐ 2026-10-15 (사용자 결정 · 같은 날 되돌림) — 지난 회차도 「이 일정만」으로 옮길 수 있다(순서 규칙만).
    const disabled = futureReason ? { future: futureReason } : {};
    const scope = await pickScope(e, 'move', disabled, pastOpened ? { future: tCalendar('calendar.recurrence.hint_all_move') } : undefined);
    if (!scope) return;

    // 낙관적 미리보기 — scope 가 가리키는 회차들을 같은 delta 만큼 화면에서 먼저 옮긴다.
    // (interval·freq 는 그대로고 기준 시각만 밀리는 것이라, future/all 도 같은 delta 로 근사가 맞는다.)
    // ⭐ 2026-10-14 (F) — 「전체」는 지난 회차를 옮기지 않는다(서버와 같게) ⇒ 미리보기에서도 지난 회차는 그대로 둔다.
    setEvents((prev) => prev.map((ev) => (matchesScope(ev, e, scope) && !(scope !== 'this' && ev.recurrenceRule && isPastOccurrence(ev.occurrenceAt))
      ? { ...ev, startAt: new Date(new Date(ev.startAt).getTime() + ms).toISOString(), endAt: new Date(new Date(ev.endAt).getTime() + ms).toISOString() }
      : ev)));

    // 2026-09-14 사용자 지시 — 이동은 실행 취소 토스트 없이 **곧바로** 저장한다.
    try {
      await moveEvent(e.id, { startAt: newStart, endAt: newEnd }, { scope, occurrenceAt: e.occurrenceAt });
      await refresh();
    } catch (err) {
      // 반복 이동은 서버가 거절할 수 있는 사유(뒤쪽 예외 존재 등)가 있어 조용히 넘기지 않는다.
      await refresh();
      const fe = describeCalendarError(err, 'move');
      void layerAlert(friendlyErrorBody(fe), { title: fe.title });
    }
  }

  return (
    <TeamCalendarIdsProvider ids={teamCalendarIds}>
    {/* 일정 칩 툴팁(제목·유형·캘린더·작성자, 2026-09-09) — 칩이 마우스/포커스에 반응하는 것
        외엔 렌더 무영향. ⚠ 여기(최상위)에 두는 까닭: 아래 `.calendar-view-anim` 은 날짜가
        바뀔 때마다 리마운트하는데, Provider 가 그 «안»에 있으면 툴팁도 함께 사라진다. */}
    <EventTooltipProvider calendars={calendars}>
    <div
      className={`calendar-page ${CAL_SHELL_ENABLED
        ? `calendar-page--shell${sideOpen ? '' : ' is-side-collapsed'}`
        : 'calendar-page--full'}`}
      style={shellStyle}
    >
      {/* 3열 셸 — 좌측 사이드바(미니 캘린더 + 다가올 일정 + 캘린더 목록). 참조 all-frame.html L1130~1162
          calendar-side-collapse-anim — 접힘 시 언마운트하지 않는다. 전역 NavBar 와 동일하게 상시
          마운트 + 루트의 `is-side-collapsed` 클래스로 폭을 전이시켜야 슬라이드가 재생된다.
          (접힘 상태의 내부 요소는 CSS visibility:hidden 이 포커스·리더에서 제외) */}
      {CAL_SHELL_ENABLED && (
        <aside className="cal-side" aria-label={tCalendar('calendar.shell.sidebar_heading')}>
          <div className="cal-side-head">
            <span className="cal-side-title">{tCalendar('calendar.shell.sidebar_heading')}</span>
            <button
              type="button"
              className="cal-side-toggle"
              onClick={() => setSideOpen(false)}
              aria-label={tCalendar('calendar.shell.sidebar_collapse')}
              title={tCalendar('calendar.shell.sidebar_collapse')}
            >
              <ChevronsLeft size={14} />
            </button>
          </div>

          <CalendarMiniMonth
            month={cursor}
            selected={selectedDate}
            eventDays={eventDays}
            onSelect={selectDay}
            onNavigate={navigate}
          />

          <div className="cal-side-scroll">
            {/* 구 필터 드롭다운의 캘린더 목록 — 상시 노출로 이동(표시 토글·생성·삭제 동작 동일)
                ⭐ 2026-10-13 사용자 결정 — 「다가올 일정」을 이 목록에 «병합»했다(옛 가지 191323cf8).
                  각 캘린더 줄 아래에 그 캘린더의 다가올 일정이 붙는다. `CalendarUpcoming` 부품은
                  파일로 남겨 두었다(다른 시험이 그 부품을 직접 그린다) — 이 화면에서만 안 쓴다. */}
            <div className="cal-side-cals">
              <CalendarSidebar
                onCalendarsChanged={(cals) => { setTeamCalendarIds(teamIdsOf(cals)); setCalendars(cals); void refresh(); }}
                upcomingEvents={upcomingEvents}
                onSelectUpcoming={selectDay}
              />
            </div>
          </div>
        </aside>
      )}

      {/* calendar-shell-resize — 사이드바 우측 경계. 사이드바가 펼쳐져 있을 때만 렌더. */}
      {CAL_SHELL_ENABLED && sideOpen && (
        <CalendarResizer
          edge="side"
          width={sideWidth}
          min={CAL_SIDE_MIN_WIDTH}
          max={CAL_SIDE_MAX_WIDTH}
          defaultWidth={CAL_SIDE_DEFAULT_WIDTH}
          label={tCalendar('calendar.shell.sidebar_resize')}
          onResize={setSideWidth}
        />
      )}

      <main className="calendar-main">
        <header className="calendar-header">
          <div className="calendar-header-nav">
            {CAL_SHELL_ENABLED && !sideOpen && (
              <button
                type="button"
                className="cal-side-expand"
                onClick={() => setSideOpen(true)}
                aria-label={tCalendar('calendar.shell.sidebar_expand')}
                title={tCalendar('calendar.shell.sidebar_expand')}
              >
                <ChevronsRight size={14} />
              </button>
            )}
            {/* 레일 전역 승격 후에는 펼치기 손잡이도 전역(RightRailHost)이 담당한다. */}
            {!SHELL_RIGHTRAIL_GLOBAL && CAL_SHELL_ENABLED && !railOpen && (
              <button
                type="button"
                className="cal-side-expand"
                onClick={() => setRailOpen(true)}
                aria-label={tCalendar('calendar.shell.rightrail_expand')}
                title={tCalendar('calendar.shell.rightrail_expand')}
              >
                <ChevronsLeft size={14} />
              </button>
            )}
            <button
              type="button"
              className="calendar-nav-button"
              onClick={() => navigate(-1)}
              aria-label={tCalendar('calendar.nav.prev')}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="calendar-nav-button"
              onClick={() => navigate(1)}
              aria-label={tCalendar('calendar.nav.next')}
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className="calendar-today-button"
              onClick={goToToday}
            >
              {tCalendar('calendar.nav.today')}
            </button>
          </div>
          {/*
            헤더 제목을 누르면 «날짜 고르개»가 열린다 (2026-09-14 사용자 요청 · 2026-09-29 반영).
            월간은 연/월만 고르면 되지만(그 달로 이동), 주간·일간은 달만 골라선 원하는 주·날짜까지
            다시 옮겨야 했다 — 달력을 펼쳐 «주 한 줄» 또는 «하루»를 바로 고르게 한다.
          */}
          <div className="calendar-title-wrap">
            {/* key={headerLabel} — 월/주/일 이동 시 타이틀 리마운트로 전환 모션 재생 (v2.11.4) */}
            <h1 className="calendar-month-title" key={headerLabel}>
              <button
                type="button"
                className="calendar-title-picker-btn"
                onClick={() => setTitlePickerOpen((v) => !v)}
                aria-haspopup="dialog"
                aria-expanded={titlePickerOpen}
                aria-label={tCalendar('calendar.nav.jump_to_date')}
              >
                <CalendarIcon size={18} /> {headerLabel} <ChevronDown size={14} aria-hidden />
              </button>
            </h1>
            {titlePickerOpen && (
              <>
                <div className="calendar-title-picker-backdrop" onClick={() => setTitlePickerOpen(false)} aria-hidden />
                <div className="calendar-title-picker-pop" role="dialog" aria-label={tCalendar('calendar.nav.jump_to_date')}>
                  {activeView === 'month' ? (
                    <MonthYearPicker
                      year={cursor.getFullYear()}
                      month={cursor.getMonth()}
                      onPick={jumpToMonth}
                    />
                  ) : (
                    <HeaderDatePicker
                      mode={activeView === 'week' ? 'week' : 'day'}
                      /*
                        ⚠ 정본은 여기서 `cursor.getDate() + 3` 을 넘긴다 — 그쪽 주간 cursor 는
                          «연속 스크롤의 화면 왼쪽 끝»이라 가운데를 봐야 지금 주가 잡혔다.
                          main 의 주간 cursor 는 «그 주 안의 어떤 날»이고 range 계산이
                          `cursor.getDay()` 를 빼 일요일을 구한다 ⇒ 보정 없이 그대로 넘긴다.
                      */
                      value={cursor}
                      onPick={jumpToDate}
                      // 주간 — 지금 주 줄 띠 안에 «선택한 날»을 따로 표시한다(2026-10-30).
                      selected={CAL_SHELL_ENABLED ? selectedDate : undefined}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div className="calendar-header-actions">
            {/* 캘린더·필터 드롭다운 (구 사이드바: 다중캘린더 표시토글 + 유형 범례 흡수) */}
            {/*
              일정 검색 (2026-09-09 정본) — 필터를 «존중»한다. 끈 유형·「내 일정만」이 결과에도 걸린다.
              ⏸ `teamMembers` prop 은 «안 넘긴다» — 서버가 그 파라미터를 안 받는다(2판-b).
                정본의 prop 은 옵셔널이라 안 넘겨도 그대로 돈다.
            */}
            <EventSearch
              onSelect={handleSearchSelect}
              calendarIds={visibleCalendarIds}
              teamMembers={showTeamMembers}
              filterFn={searchFilter}
            />
            <div className="calendar-filter">
              <button
                type="button"
                className={`calendar-filter-btn${filterOpen ? ' is-open' : ''}`}
                onClick={() => setFilterOpen((v) => !v)}
                aria-expanded={filterOpen}
              >
                <Filter size={14} /> {CAL_SHELL_ENABLED
                  ? tCalendar('calendar.filter.button_types')
                  : tCalendar('calendar.filter.button')}
              </button>
              {filterOpen && (
                <>
                  <div className="calendar-filter-backdrop" onClick={() => setFilterOpen(false)} aria-hidden />
                  <div className="calendar-filter-pop" role="dialog" aria-label={tCalendar('calendar.filter.button')}>
                    {/* 3열 셸 ON 이면 캘린더 목록은 사이드바가 담당 — 드롭다운은 유형 범례만(중복 렌더 방지) */}
                    {!CAL_SHELL_ENABLED && (
                      <CalendarSidebar
                        onCalendarsChanged={(cals) => { setTeamCalendarIds(teamIdsOf(cals)); void refresh(); }}
                      />
                    )}
                    {/*
                      「내가 포함된 일정만」 — 2026-09-09 결정으로 사이드바 알약 토글에서
                      이 팝오버 안의 스위치로 옮겼다.
                      ⏸ 「팀원 일정도 보기」는 «여기 안 그린다» — 서버가 그 파라미터를 안 받아
                        켜도 아무 일이 안 일어난다(store 의 showTeamMembers 주석에 만료 조건).
                    */}
                    <button
                      type="button"
                      className={`calendar-filter-onlymine${onlyMine ? ' is-active' : ''}`}
                      aria-pressed={onlyMine}
                      onClick={toggleOnlyMine}
                    >
                      <span>{tCalendar('calendar.filter.only_mine')}</span>
                    </button>
                    {/*
                      ⭐ 「팀원 일정도 보기」 (2026-10-13) — 서버가 `teamMembers=1` 을 받게 되어 «그린다».
                      내 팀(조직도팀·프로젝트팀)의 다른 구성원이 만들었거나 참석하는 일정. 기본 꺼짐.
                      ⚠ 모양은 바로 위 「내 일정만 보기」와 같은 스위치(같은 클래스) — 새 CSS 없음.
                    */}
                    <button
                      type="button"
                      className={`calendar-filter-onlymine${showTeamMembers ? ' is-active' : ''}`}
                      aria-pressed={showTeamMembers}
                      onClick={toggleShowTeamMembers}
                    >
                      <span>{tCalendar('calendar.filter.team_members')}</span>
                    </button>
                    {/*
                      유형 — 2026-09-09 결정으로 «범례»에서 «필터»가 됐다. 끄면 그리드에서 빠진다.
                      ⭐ 끈 목록은 calendarStore(persist)에 남아 새로고침을 넘어 유지된다.
                    */}
                    <div className="calendar-legend">
                      <div className="calendar-legend-heading">{tCalendar('calendar.filter.legend_heading')}</div>
                      {Object.entries(TYPE_COLOR_MAP).map(([key, meta]) => {
                        const off = hiddenTypes.has(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`calendar-legend-item calendar-legend-item--btn${off ? ' is-off' : ''}`}
                            aria-pressed={!off}
                            onClick={() => toggleType(key)}
                          >
                            <span className="calendar-legend-ico" style={{ color: meta.color }} aria-hidden>
                              <TypeIcon type={key} size={14} />
                            </span>
                            {meta.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="calendar-view-toggle">
              <button
                type="button"
                className={`calendar-view-button ${activeView === 'month' ? 'calendar-view-button--active' : ''}`}
                onClick={() => switchView('month')}
              >{tCalendar('calendar.view.month')}</button>
              <button
                type="button"
                className={`calendar-view-button ${activeView === 'week' ? 'calendar-view-button--active' : ''}`}
                onClick={() => switchView('week')}
              >{tCalendar('calendar.view.week')}</button>
              <button
                type="button"
                className={`calendar-view-button ${activeView === 'day' ? 'calendar-view-button--active' : ''}`}
                onClick={() => switchView('day')}
              >{tCalendar('calendar.view.day')}</button>
            </div>

            <button
              type="button"
              className="calendar-new-button"
              onClick={() => { setCreateType(undefined); setCreateDate(null); setCreateStart(null); setCreateEnd(null); setCreating(true); }}
            >
              <Plus size={14} /> {tCalendar('calendar.btn.create')}
            </button>
          </div>
        </header>

        {error && (
          <div className="calendar-error" role="alert">
            {error}
            <button type="button" onClick={refresh}>다시 시도</button>
          </div>
        )}

        {/*
          v2.11.4 — 뷰/기간 전환 시 래퍼 리마운트로 페이드+슬라이드 진입 재생.

          ⛔⛔ 주간은 «날짜»를 열쇠에서 뺀다 (C-2b · 2026-09-29 · 재서 확인).
            이 열쇠에 날짜가 들어 있으면 이전/다음을 누를 때마다 래퍼가 리마운트되고, 그러면
            «연속 스크롤 껍데기까지 새로 마운트»된다 ⇒ 스크롤로 쌓아 둔 날짜 창이 버려지고
            그 자리에서 조회가 «한 번 더» 나간다(시험 ③ 이 4번째 요청을 잡아냈다).
            ⇒ 주간에서는 리마운트 대신 «그 날짜로 스크롤»한다(아래 `scrollToDate` effect).
          ⚠ 그래서 «주간에서만» 이전/다음의 페이드+슬라이드가 재생되지 않는다 — 대신 격자가
            그 주로 굴러간다. 정본(`feature/dev-kyeongeun`)에는 이 래퍼 자체가 없다(재서 확인).
            월간·일간은 종전 그대로다.
        */}
        <div
          className="calendar-view-anim"
          /*
           * ⛔⛔ **M-2 — 월간을 이 key 에서 뺐다** (2026-10-02).
           *
           * 이 key 는 뷰·날짜가 바뀔 때 **다시 마운트**해 전환 모션을 재생한다. 그런데 월간이
           * 연속 창이 된 뒤로는 ⇒ ⛔ `cursor` 가 바뀔 때마다 **쌓아 둔 날짜 창이 통째로 버려진다**
           * (주간이 같은 함정을 먼저 겪었다 — 그래서 주간은 이미 빠져 있다).
           * ⇒ ★ 이제 월간도 **ref 로 미끄러져 간다**(위 `monthRef` effect) ⇒ 다시 마운트하지 않는다.
           * ⚠ 남은 것은 «일간»뿐이다 — 그쪽은 하루치라 다시 그려도 버릴 창이 없다.
           */
          key={activeView === 'week' || activeView === 'month'
            ? activeView
            : `${activeView}:${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`}
        >
          {activeView === 'month' && (
            <MonthView
              cursor={cursor}
              events={visibleEvents}
              holidays={holidays}
              loading={loading}
              onEventClick={(e) => setSelectedEvent(e)}
              onDateClick={openDayPopover}
              selectedDate={CAL_SHELL_ENABLED ? selectedDate : null}
              // ⭐ M-1 — 주간과 «같은» 누적기를 쓴다(useCoalescedRange ＋ unionIsoRange).
              onNeedRange={requestStreamRange}
              // ⭐ M-2 — 제목이 「화면에 걸린 달」을 따라가고, 이전/다음은 ref 로 미끄러져 간다.
              ref={monthRef}
              onVisibleMonthChange={(y, m) => setVisibleMonth({ y, m })}
              /*
                ⭐ M-4 — 끌어서 날짜 옮기기. ★ 주간이 쓰는 «그 함수 그대로»다
                  (`handleEventMove` — 꼴이 같고, 반복 일정 범위 묻기·되돌리기가 이미 그 안에 있다).
              */
              onEventMove={handleEventMove}
              // ⭐ 칸 호버 «＋» — 그 날짜로 바로 만들기(옛 가지 7655e3bd1). 시각은 레일의 「추가」와 같은 09:00.
              onQuickCreate={(d) => { const dd = new Date(d); dd.setHours(9, 0, 0, 0); openCreateOnDate(dd); }}
            />
          )}
          {/*
            주간 — «끊기지 않는 가로 연속 스크롤»(C-2b · 2026-09-29).
            ⛔ 그리는 일은 여전히 `TimeGridView` 가 한다 — `TimeGridStream` 은 그것을 감싸 날짜
              스트림과 가로 스크롤만 맡는 껍데기다(겹침 배치·드래그·아바타는 두 벌이 아니다).
            ⚠ 되돌릴 때는 이 블록을 `<WeekView cursor={cursor} …/>` 로 되돌리고 위 `streamRange`·
              `visibleDay`·두 effect 를 지우면 된다 — `TimeGridView.tsx` 는 안 건드렸다.
            ⚠ 일간은 이 판의 대상이 «아니다» — 이미 `DayAgendaView`(시간축+표)로 바뀌어 있고
              그것은 2026-09-09 사용자 결정이다. 되돌리지 않는다.
          */}
          {activeView === 'week' && (
            <TimeGridStream
              ref={streamRef}
              initialDate={cursor}
              perView={7}
              events={visibleEvents}
              loading={loading}
              onEventClick={(e) => setSelectedEvent(e)}
              onEventMove={handleEventMove}
              onSlotCreate={openCreateOnSlot}
              onNeedRange={requestStreamRange}
              onVisibleDayChange={setVisibleDay}
              allDayFocus={headerWeek}
              // ⭐ 날짜 머리를 누르면 그 날짜를 고른다 — 우측 레일이 그 날을 보여 준다(옛 가지 52f74393c).
              onDayHeadClick={CAL_SHELL_ENABLED ? selectDay : undefined}
              selectedDate={CAL_SHELL_ENABLED ? selectedDate : null}
            />
          )}
          {/*
            일간 — 24시간 격자 대신 「시간 축 + 표」 (2026-09-09 사용자 결정 · 2026-09-28 반영).
            ⛔ **여기서 `onEventMove` 를 안 넘긴다** — 표에는 끌 자리가 없다. 그래서 일간에서
              「끌어서 시각 옮기기」가 사라진다(정본도 그렇게 한다). **주간에는 그대로 남는다.**
            ⚠ `DayView`(TimeGridView 의 1열 판)는 이제 **제품 호출자가 0** 이다. 그래도 그 파일에서
              지우지 않았다 — `TimeGridView.tsx` 는 «주간»의 파일이고, 이 판은 주간을 안 건드린다.
              되돌릴 때는 이 블록과 위 import 만 되돌리면 된다.
            ⇒ Ⓐ 줄이 다 끝나고도 부르는 곳이 없으면 그때 지운다.
          */}
          {activeView === 'day' && (
            <DayAgendaView
              date={cursor}
              events={visibleEvents}
              calendars={calendars}
              loading={loading}
              myUserId={myUserId !== null ? String(myUserId) : null}
              onEventClick={(e) => setSelectedEvent(e)}
            />
          )}
        </div>
      </main>

      {/* calendar-shell-resize — 선택일 레일 좌측 경계(좁은 화면에선 레일과 함께 CSS 로 숨김). */}
      {CAL_SHELL_ENABLED && (
        <CalendarResizer
          edge="rail"
          width={railWidth}
          min={CAL_RAIL_MIN_WIDTH}
          max={CAL_RAIL_MAX_WIDTH}
          defaultWidth={CAL_RAIL_DEFAULT_WIDTH}
          label={tCalendar('calendar.shell.rail_resize')}
          onResize={setRailWidth}
        />
      )}

      {/*
        3열 셸 — 우측 선택일 레일. 참조 all-frame.html L1176~1185

        ⛔ **일간 뷰에서는 그리지 않는다** (2026-09-09 사용자 결정 · 2026-09-28 반영).
          가운데 표가 «같은 날의 같은 목록»을 이미 보여 줘서 레일이 중복이다.

        ⚠ 그리고 그것만이 아니었다 — 레일은 `selectedDate` 를 쓰는데 일간 격자는 `cursor` 를 쓴다.
          둘이 어긋나면(실측 2026-09-28: 일간이 30일인데 레일 머리가 28일) 레일의
          「이 날짜에 일정 추가」가 **보고 있지 않은 날**에 일정을 만든다 — 만들고 나서야 안다.
        ⇒ 그래서 이 한 줄이 «중복»과 «조용히 틀리던 단추»를 함께 치운다.

        ⚠ 잃는 것 — 일간에서 「보는 날짜로 미리 채워 만들기」는 없어진다.
          남는 길은 툴바 「＋ 일정 추가」이고 그것은 날짜를 «안 채운다»(`setCreateDate(null)`).
          격자 빈 슬롯 클릭 생성은 애초에 없다. 되살릴지는 별도 결정이다.
      */}
      {CAL_SHELL_ENABLED && activeView !== 'day' && (
        <CalendarDayRail
          date={selectedDate}
          events={eventsOnDay(visibleEvents, selectedDate)}
          onEventClick={(e) => setSelectedEvent(e)}
          onCreate={(d) => { const dd = new Date(d); dd.setHours(9, 0, 0, 0); openCreateOnDate(dd); }}
          calendars={calendars}
        />
      )}

      {/* ⑤ 우측 아이콘 레일 — 공통 레이아웃 요소. 참조 all-frame.html L1186~1198.
          표준 껍데기 RightRail(design-20260717 §5-⑤) 을 캘린더에서 opt-in 한다.
          ⚠ 메모·즐겨찾기는 앱에 기능 자체가 없어 자리만 잡고 비활성으로 둔다(눌리는 척 금지).
          ⚠ 2026-07-24 — 레일이 **전역 공통**(AppLayout 의 RightRailHost)으로 승격되면서
             여기서는 중복 방지로 미렌더한다. 아래 블록은 롤백 플래그
             (`VITE_SHELL_RIGHTRAIL='0'`) 경로로만 살아나는 기존 구현이다(무회귀 보존). */}
      {!SHELL_RIGHTRAIL_GLOBAL && CAL_SHELL_ENABLED && railOpen && (
        <div className="cal-right-rail">
          <RightRail
            profile={
              myUserId !== null ? (
                <Link
                  to="/me"
                  className="cal-right-rail-profile"
                  title={tCalendar('calendar.shell.my_profile')}
                  aria-label={tCalendar('calendar.shell.my_profile')}
                >
                  {/* 점 색 = 상태바에서 고른 근무 상태(2026-08-12). 미설정이면 종전 초록 그대로. */}
                  <MyStatusAvatar userId={myUserId} name={myName} src={myAvatarUrl} size="sm" />
                </Link>
              ) : undefined
            }
            {...(MEMO_PANEL_ENABLED
              // ⑤ 메모 실기능(design-20260724) — 패널은 AppLayout 이 전역 소유, 여기선 토글만.
              ? { onMemo: openMemoPanel }
              // 플래그 OFF 롤백 경로 — 기존 비활성 플레이스홀더 유지(무회귀).
              : { memoDisabled: true, memoTitle: tCalendar('calendar.shell.coming_soon') })}
            collapseLabel={tCalendar('calendar.shell.rightrail_collapse')}
            onCollapse={() => setRailOpen(false)}
          >
            <div className="cal-right-rail-favlabel">{tCalendar('calendar.shell.favorites')}</div>
            <button
              type="button"
              className="cal-right-rail-favadd"
              disabled
              title={tCalendar('calendar.shell.coming_soon')}
              aria-label={`${tCalendar('calendar.shell.favorites')} — ${tCalendar('calendar.shell.coming_soon')}`}
            >
              <Plus size={13} />
            </button>
          </RightRail>
        </div>
      )}

      {dayPopover && (
        <DayPopover
          date={dayPopover.date}
          anchor={dayPopover.anchor}
          events={eventsOnDate(dayPopover.date)}
          onClose={() => setDayPopover(null)}
          onOpenEvent={(e) => { setDayPopover(null); setSelectedEvent(e); }}
          onCreate={createFromPopover}
          onToggleDone={(e) => { void toggleDone(e); }}
        />
      )}

      {shouldRenderCreate && (
        <EventCreateModal
          /*
            ⛔ 툴바 「＋ 일정 추가」는 날짜를 안 채우므로(`setCreateDate(null)`) 여기 기본값이
              그대로 창에 뜬다. 그런데 월간 `cursor` 는 «그 달 1일»이라, 15일을 골라 두고 눌러도
              창이 「9월 1일」로 열렸다(2026-09-28 실측 · 운영 보고 §5-4).
            ⇒ 월간에서는 「보고 있는 날」이 선택일이다 — 레일이 그 날짜를 글자로 띄우고 있다.
              주간·일간의 cursor 는 이미 «그 날»이라 바꿀 것이 없다.
            ⭐ c9(일간에서 레일을 안 그린다)가 「보는 날짜로 미리 채워 만들기」를 없애며
              「남는 길은 툴바이고 그것은 날짜를 안 채운다 — 되살릴지는 별도 결정」이라 적어
              뒀는데, 이 줄이 그 결정이다.
          */
          initialDate={createDate ?? (CAL_SHELL_ENABLED && activeView === 'month' ? selectedDate : cursor)}
          initialStart={createStart}
          initialEnd={createEnd}
          presetType={createType}
          isLeaving={isCreateLeaving}
          onClose={() => { setCreating(false); setCreateType(undefined); setCreateStart(null); setCreateEnd(null); }}
          onCreated={async () => {
            setCreating(false);
            setCreateType(undefined);
            setCreateStart(null);
            setCreateEnd(null);
            await refresh();
          }}
          /* ⇒ 쓰기는 됐고 회의실만 실패한 때 — 창은 두고 달력만 다시 읽는다(2026-10-01). */
          onRefreshCalendar={() => { void refresh(); }}
        />
      )}

      {shouldRenderDetail && detailSnap && (
        <EventDetailModal
          event={detailSnap}
          isCreator={Boolean(isCreatorOfSelected)}
          myUserId={myUserId !== null ? String(myUserId) : null}
          isLeaving={isDetailLeaving}
          calendars={calendars}
          onClose={() => setSelectedEvent(null)}
          onDelete={() => onDelete(detailSnap)}
          onUpdated={async () => {
            setSelectedEvent(null);
            await refresh();
          }}
          // 2026-10-19 — 참석 여부를 골라도 상세 창은 그대로(사용자 지시). 달력만 다시 읽는다.
          onRsvpChange={() => { void refresh(); }}
          onEdit={() => {
            // 상세 → 수정: 상세 닫고 수정 모달 오픈(같은 이벤트).
            setEditingEvent(detailSnap);
            setSelectedEvent(null);
          }}
          // 2026-10-13 (옛 가지 a2aa18e96) — 상세에서 알림을 바꾸면 목록의 reminders 를 바로 고친다
          // ⇒ 일간 표가 새로고침 없이 따라온다. 반복 일정의 회차는 master 와 같은 id 라(occurrenceAt 만 다르다)
          //   같은 id 전부를 고친다 — 서버도 회차가 master 의 알림을 «공유»한다(calendar-list-reminders).
          onRemindersChange={(eventId, reminders) => {
            setEvents((prev) => (prev.some((ev) => ev.id === eventId)
              ? prev.map((ev) => (ev.id === eventId ? { ...ev, reminders } : ev))
              : prev));
          }}
        />
      )}

      {shouldRenderEdit && editSnap && (
        <EventCreateModal
          editEvent={editSnap}
          occurrenceNeighbors={occurrenceNeighborsIn(events, editSnap)}
          initialDate={cursor}
          isLeaving={isEditLeaving}
          onClose={() => setEditingEvent(null)}
          onCreated={async () => {
            setEditingEvent(null);
            await refresh();
          }}
          onRefreshCalendar={() => { void refresh(); }}
        />
      )}

      {/* 반복 일정 범위 선택 — imperative(askRecurrenceScope) 호출을 받는 호스트. portal 이라
          위치는 렌더 트리와 무관하고, 큐가 비어 있으면 아무것도 그리지 않는다. */}
      <RecurrenceScopeHost />
    </div>
    </EventTooltipProvider>
    </TeamCalendarIdsProvider>
  );
}
